import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Alert from '@/components/base/Alert';
import { adminApi } from '@/lib/adminApi';
import type { AdminSettings } from '@/lib/adminApi';
import { AdminCard, ErrorBlock, LoadingBlock } from './ui';

const emptySettings: AdminSettings = {
  platformName: 'Monexa',
  supportEmail: '',
  adminEmail: '',
  maxLoginAttempts: 2,
  telegramEnabled: false,
  telegramChatId: '',
  telegramBotToken: '',
  siteUrl: '',
  senderName: 'Monexa',
  emailSender: '',
  emailDomain: '',
  emailConfigured: false,
};

export default function SettingsTab({ token }: { token: string }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<AdminSettings>(emptySettings);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [role, setRole] = useState('');

  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMessage, setPwdMessage] = useState('');
  const [pwdError, setPwdError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.settingsGet(token);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setSettings({ ...emptySettings, ...(res.settings as AdminSettings) });
      const admin = res.admin as { role?: string } | undefined;
      if (admin?.role) setRole(admin.role);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    const res = await adminApi.settingsUpdate(token, { ...settings });
    setBusy(false);
    if (!res.ok) {
      setError(res.code === 'INVALID_EMAIL' ? t('admin.settings.invalidEmail') : t('errors.server'));
      return;
    }
    setMessage(t('admin.settings.generalSaved'));
    load();
  };

  const changePassword = async () => {
    if (pwdBusy) return;
    setPwdError('');
    setPwdMessage('');
    if (pwd.next !== pwd.confirm) {
      setPwdError(t('admin.settings.passwordMismatch'));
      return;
    }
    setPwdBusy(true);
    const res = await adminApi.passwordChange(token, pwd.current, pwd.next);
    setPwdBusy(false);
    if (!res.ok) {
      setPwdError(
        res.code === 'WEAK_PASSWORD'
          ? t('admin.settings.passwordWeak')
          : res.code === 'INVALID_PASSWORD'
            ? t('admin.settings.passwordWrong')
            : t('errors.server'),
      );
      return;
    }
    setPwd({ current: '', next: '', confirm: '' });
    setPwdMessage(t('admin.settings.passwordDone'));
  };

  if (loading) return <LoadingBlock />;
  if (failed) return <ErrorBlock onRetry={load} />;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
      <AdminCard title={t('admin.settings.general')} subtitle={t('admin.settings.subtitle')}>
        <div className="flex flex-col gap-3.5">
          <Input
            label={t('admin.settings.platformName')}
            name="platformName"
            value={settings.platformName}
            onChange={(event) => setSettings({ ...settings, platformName: event.target.value })}
            icon="ri-apps-line"
          />
          <Input
            label={t('admin.settings.supportEmail')}
            name="supportEmail"
            type="email"
            value={settings.supportEmail}
            onChange={(event) => setSettings({ ...settings, supportEmail: event.target.value })}
            icon="ri-customer-service-2-line"
          />
          <Input
            label={t('admin.settings.adminEmail')}
            name="adminEmail"
            type="email"
            value={settings.adminEmail}
            onChange={(event) => setSettings({ ...settings, adminEmail: event.target.value })}
            icon="ri-mail-lock-line"
            hint={t('admin.settings.adminEmailHint')}
          />
          <Input
            label={t('admin.settings.maxAttempts')}
            name="maxLoginAttempts"
            type="number"
            min="2"
            max="10"
            value={String(settings.maxLoginAttempts)}
            onChange={(event) => setSettings({ ...settings, maxLoginAttempts: Number(event.target.value) })}
          />
          <Input
            label={t('admin.settings.senderName')}
            name="senderName"
            value={settings.senderName}
            onChange={(event) => setSettings({ ...settings, senderName: event.target.value })}
            icon="ri-mail-line"
            hint={t('admin.settings.senderNameHint')}
          />
          <div className="flex flex-col gap-2 rounded-xl border border-background-200 bg-background-100 px-3.5 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground-600 font-label">
                <span className="flex items-center justify-center w-3.5 h-3.5 text-primary-700">
                  <i className="ri-mail-send-line text-[13px]" aria-hidden="true" />
                </span>
                {t('admin.settings.emailSender')}
              </span>
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10.5px] font-semibold font-label whitespace-nowrap ${
                  settings.emailConfigured
                    ? 'border-primary-200 bg-primary-50 text-primary-800'
                    : 'border-background-300 bg-background-200 text-foreground-600'
                }`}
              >
                {settings.emailConfigured ? t('admin.settings.emailReady') : t('admin.settings.emailMissing')}
              </span>
            </div>
            <span className="break-all text-[12.5px] font-semibold text-foreground-900 font-body">
              {settings.emailSender || '—'}
            </span>
          </div>
          {role ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-100 px-3.5 py-2.5">
              <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground-600 font-label">
                <span className="flex items-center justify-center w-3.5 h-3.5 text-primary-700">
                  <i className="ri-shield-user-line text-[13px]" aria-hidden="true" />
                </span>
                {t('admin.settings.role')}
              </span>
              <span className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-[10.5px] font-semibold text-primary-800 font-label whitespace-nowrap">
                {t(`admin.roles.${role}`)}
              </span>
            </div>
          ) : null}
          {message ? <Alert tone="success">{message}</Alert> : null}
          {error ? <Alert tone="error">{error}</Alert> : null}
          <Button fullWidth loading={busy} onClick={save} icon={<i className="ri-save-3-line" aria-hidden="true" />}>
            {t('admin.common.save')}
          </Button>
        </div>
      </AdminCard>

      <div className="flex flex-col gap-5">
        <AdminCard title={t('admin.settings.notifications')} subtitle={t('admin.settings.telegramHint')}>
          <div className="flex flex-col gap-3.5">
            <button
              type="button"
              onClick={() => setSettings({ ...settings, telegramEnabled: !settings.telegramEnabled })}
              className={`inline-flex items-center justify-between gap-3 h-11 px-4 rounded-xl border text-[13px] font-semibold font-label transition-colors cursor-pointer ${
                settings.telegramEnabled
                  ? 'border-primary-300 bg-primary-50 text-primary-800'
                  : 'border-background-300 bg-background-100 text-foreground-600'
              }`}
            >
              {t('admin.settings.telegramEnabled')}
              <span className={`flex items-center justify-center w-4 h-4 ${settings.telegramEnabled ? 'text-primary-700' : 'text-foreground-400'}`}>
                <i
                  className={settings.telegramEnabled ? 'ri-toggle-fill text-lg' : 'ri-toggle-line text-lg'}
                  aria-hidden="true"
                />
              </span>
            </button>
            <Input
              label={t('admin.settings.telegramChat')}
              name="telegramChatId"
              value={settings.telegramChatId}
              onChange={(event) => setSettings({ ...settings, telegramChatId: event.target.value })}
              placeholder="-1001234567890"
              icon="ri-send-plane-line"
            />
            <Input
              label={t('admin.settings.telegramToken')}
              name="telegramBotToken"
              type="password"
              value={settings.telegramBotToken}
              onChange={(event) => setSettings({ ...settings, telegramBotToken: event.target.value })}
              placeholder="123456789:AA..."
              icon="ri-key-2-line"
            />
          </div>
        </AdminCard>

        <AdminCard title={t('admin.settings.security')} subtitle={t('admin.settings.passwordRules')}>
          <div className="flex flex-col gap-3.5">
            <Input
              label={t('admin.settings.currentPassword')}
              name="currentPassword"
              type="password"
              value={pwd.current}
              onChange={(event) => setPwd({ ...pwd, current: event.target.value })}
              icon="ri-lock-line"
              autoComplete="current-password"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Input
                label={t('admin.settings.newPassword')}
                name="newPassword"
                type="password"
                value={pwd.next}
                onChange={(event) => setPwd({ ...pwd, next: event.target.value })}
                icon="ri-lock-password-line"
                autoComplete="new-password"
              />
              <Input
                label={t('admin.settings.confirmPassword')}
                name="confirmPassword"
                type="password"
                value={pwd.confirm}
                onChange={(event) => setPwd({ ...pwd, confirm: event.target.value })}
                icon="ri-lock-password-line"
                autoComplete="new-password"
              />
            </div>
            {pwdMessage ? <Alert tone="success">{pwdMessage}</Alert> : null}
            {pwdError ? <Alert tone="error">{pwdError}</Alert> : null}
            <Button
              variant="dark"
              fullWidth
              loading={pwdBusy}
              onClick={changePassword}
              icon={<i className="ri-shield-keyhole-line" aria-hidden="true" />}
            >
              {t('admin.settings.changePassword')}
            </Button>
          </div>
        </AdminCard>
      </div>
    </div>
  );
}